// @ts-nocheck
import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { Alert, IconButton, Box, Typography, Stack } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import { FormattedMessage, useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';
import useApi from '../../hooks/useApi';
import Cookies from 'universal-cookie';
import constants from '../../utils/constants';
import useSubscriptionSummaryRefreshTriggers from '../../hooks/useSubscriptionSummaryRefreshTriggers';
import { dispatchSubscriptionSummaryRefresh } from '../../utils/subscriptionSummaryRefresh';
import useSubscriptionPlans from '../../hooks/useSubscriptionPlans';
import {
  createStripeCheckoutSession,
  startChoosePlanMonthlyStripeCheckout,
  STRIPE_CHECKOUT_INSIGHT_PLAN_KEY
} from '../../utils/subscriptionRedirect';
import { getManageSubscriptionsPath } from '../../utils/subscriptionPaths';
import {
  isSubscriptionGraceStatus,
  isPaymentFailureGrace,
  isSubscriptionExpiredInSummary,
  isSubscriptionInGraceState,
  resolveMonthlyStyleGraceBannerType
} from '../../utils/subscriptionGraceStatus';
import { isLmsCreditsAllZero } from '../../utils/isLmsCreditsAllZero';
import { isInsightMonthlyUsageBillingPlan, isPayAsYouGoSubscriptionPlan } from '../../utils/apexPayAsYouGoBanner';
import useApexFeatureStatus from '../../hooks/useApexFeatureStatus';
import roleManagement from '../../utils/roleManagement';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { ErrorOutline } from '@mui/icons-material';
import { ThemeMode } from 'config';

const cookies = new Cookies();
const TRIAL_ENDING_THRESHOLD_DAYS = constants.SUBSCRIPTION.TRIAL_ENDING_BANNER_THRESHOLD_DAYS;
const LONG_DATE_OPTIONS = { year: 'numeric', month: 'long', day: 'numeric' } as const;
/** Figma: trial expiring banner (amber-50 bg, warning-main text) */
const TRIAL_ENDING_BANNER_BG_LIGHT = '#FFF8E1';
const TRIAL_ENDING_BANNER_FG = '#EF6C00';

export default function SubscriptionBanners() {
  const [isVisible, setIsVisible] = useState(false);
  const [expiryDate, setExpiryDate] = useState(null);
  const [graceEndDate, setGraceEndDate] = useState(null);
  const [remainingTrialDays, setRemainingTrialDays] = useState(null);
  const [trialExpiredDate, setTrialExpiredDate] = useState(null);
  const [bannerType, setBannerType] = useState<
    | 'trialActive'
    | 'trialEnding'
    | 'trialExpired'
    | 'monthlyExpired'
    | 'monthlyGraceChoosePlan'
    | 'monthlyGracePaymentFailure'
    | 'paygGrace'
    | 'paygGracePaymentFailure'
    | 'paygExpired'
    | 'yearlyExpired'
    | 'yearlyGrace'
    | 'yearlyGracePaymentFailure'
    | 'threeYearlyExpired'
    | 'threeYearlyGrace'
    | 'threeYearlyGracePaymentFailure'
    | 'lmsExpired'
    | null
  >(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [currentPlanData, setCurrentPlan] = useState(null);
  const { pathname, search } = useLocation();
  const { plans: subscriptionPlans, isCountrySupported } = useSubscriptionPlans();
  const canAddLmsKey = roleManagement.checkUser('addLmsKey');
  const canChangePlan = roleManagement.checkUser('showChangePlan');
  const isFetchingRef = useRef(false);
  const hasBannerResolvedRef = useRef(false);
  /** Last creditSummary `details`; re-classified (without a refetch) when Apex feature flags resolve. */
  const summaryDetailsRef = useRef(null);
  const intl = useIntl();
  const theme = useTheme();
  const { isApexEligible, isPaygMubFromApexTimestamp } = useApexFeatureStatus(false);
  const apexFeatureRef = useRef({ isApexEligible: false, isPaygMubFromApexTimestamp: false });

  useEffect(() => {
    apexFeatureRef.current = { isApexEligible, isPaygMubFromApexTimestamp };
  }, [isApexEligible, isPaygMubFromApexTimestamp]);

  const timestampToDate = (timestamp) => {
    if (!timestamp) return '';
    // Convert seconds to milliseconds if needed
    const date = new Date(timestamp * 1000);
    if (isNaN(date.getTime())) return '';
    return date.toISOString().split('T')[0]; // Returns YYYY-MM-DD
  };

  /** creditSummary `currentPlan.endDate` is milliseconds (see useSubscriptionHistory). */
  const formatCreditSummaryPlanEndDate = (endDate) => {
    if (endDate == null || endDate === '' || endDate === 0) return null;
    const iso = timestampToDate(Number(endDate) / 1000);
    if (!iso) return null;
    const [y, m, day] = iso.split('-').map((v) => parseInt(v, 10));
    const d = new Date(y, m - 1, day);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  /** Show trial expired date only when creditSummary `currentPlan.endDate` is present. */
  const resolveTrialExpiredDisplayDate = (details) => formatCreditSummaryPlanEndDate(details?.currentPlan?.endDate);

  const formatGraceDate = (date, type) => {
    if (date == null || date === '') return '';
    try {
      const d = new Date(date);
      if (Number.isNaN(d.getTime())) return '';
      if (type == 'isMonthlyPlan') {
        d.setDate(d.getDate() + 14);
      } else if (type == 'isYearlyPlan') {
        d.setDate(d.getDate() + 30);
      } else if (type == 'isThreeYearlyPlan') {
        d.setDate(d.getDate() + 30);
      }
      return d.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (e) {
      return String(date);
    }
  };

  /**
   * Classify creditSummary `details` into a banner. Network-free, so it can be re-run when the Apex
   * feature flags resolve without calling creditSummary again.
   */
  const applySummaryDetails = useCallback((details) => {
    const remainingDays = details?.remainingtrialDays ?? details?.remainingTrialDays ?? null;

    let currentPlan = details?.currentPlan ?? null;
    if (currentPlan?.planName === 'Insight Premium-ZeroDollar') {
      currentPlan = null;
    }
    setCurrentPlan(currentPlan);

    const apiLmsResponse = details?.lmsResponse ?? null;
    const hasLmsData =
      apiLmsResponse && typeof apiLmsResponse === 'object' && !Array.isArray(apiLmsResponse) && Object.keys(apiLmsResponse).length > 0;
    const hasLmsPlan = (remainingDays === null || remainingDays < 0) && hasLmsData;
    const hasSubscriptionPlan = remainingDays === null && currentPlan != null;
    const hasSubscriptionPlanAfterTrial = remainingDays != null && remainingDays < 0 && currentPlan != null;
    const hasPurchasedPlan = hasSubscriptionPlan || hasSubscriptionPlanAfterTrial || hasLmsPlan;
    const isTrialExpiredNoPlan = remainingDays != null && remainingDays < 0 && !currentPlan && !hasLmsPlan;

    const isExpiredPlan = currentPlan?.status?.toLowerCase() === 'expired';
    const isPlanInGrace = isSubscriptionGraceStatus(currentPlan?.status);
    const paymentFailureAt = currentPlan?.paymentFailureAt;
    const graceFromPaymentFailure = isPaymentFailureGrace(paymentFailureAt);

    const planTypeText = String(currentPlan?.planId).toLowerCase();
    const frequency = String(currentPlan?.frequency ?? '').trim();
    const isYearlyPlan = planTypeText.indexOf('1-year') > -1 || planTypeText.indexOf('1 year') > -1 || planTypeText.indexOf('1yr') > -1;

    const isThreeYearlyPlan =
      planTypeText.indexOf('3-year') > -1 || planTypeText.indexOf('3 year') > -1 || planTypeText.indexOf('3yr') > -1;

    const isMonthlyPlan = planTypeText.indexOf('monthly') > -1 || planTypeText.indexOf('month') > -1;

    const creditSummaryApex = details?.Apex ?? details?.apex ?? null;
    const paygPlanLike = currentPlan ? { ...currentPlan, rawData: currentPlan } : null;
    const { isApexEligible: apexEligible, isPaygMubFromApexTimestamp: paygPurchased } = apexFeatureRef.current;
    const isPaygPlan = isPayAsYouGoSubscriptionPlan(paygPlanLike, creditSummaryApex, apexEligible, paygPurchased);
    const paygPlanStatusLike = paygPlanLike;
    const isPaygExpiredState = isSubscriptionExpiredInSummary(paygPlanStatusLike);
    const isPaygGraceState = isSubscriptionInGraceState(paygPlanStatusLike);

    setRemainingTrialDays(remainingDays);

    const isTrialEnding =
      remainingDays !== null && remainingDays !== undefined && remainingDays >= 0 && remainingDays <= TRIAL_ENDING_THRESHOLD_DAYS;
    const isTrialActive = remainingDays !== null && remainingDays !== undefined && remainingDays > TRIAL_ENDING_THRESHOLD_DAYS;

    if (isExpiredPlan && isThreeYearlyPlan) {
      setBannerType('threeYearlyExpired');
      setIsVisible(true);
      const expiration = new Date(currentPlan?.endDate).toLocaleDateString('en-US', LONG_DATE_OPTIONS);
      setExpiryDate(expiration);
    } else if (isExpiredPlan && isYearlyPlan) {
      setBannerType('yearlyExpired');
      setIsVisible(true);
      const expiration = new Date(currentPlan?.endDate).toLocaleDateString('en-US', LONG_DATE_OPTIONS);
      setExpiryDate(expiration);
    } else if (isTrialExpiredNoPlan) {
      setBannerType('trialExpired');
      setTrialExpiredDate(resolveTrialExpiredDisplayDate(details));
      setIsVisible(true);
    } else if (isTrialActive && !hasPurchasedPlan) {
      setExpiryDate(remainingDays);
      setBannerType('trialActive');
      setIsVisible(true);
    } else if (isTrialEnding && !hasPurchasedPlan) {
      setBannerType('trialEnding');
      setIsVisible(true);
    } else if (isPlanInGrace && isYearlyPlan) {
      setBannerType(graceFromPaymentFailure ? 'yearlyGracePaymentFailure' : 'yearlyGrace');
      const expirationDate = timestampToDate(currentPlan.endDate / 1000);
      const graceEndDate = formatGraceDate(expirationDate, 'isYearlyPlan');
      setGraceEndDate(graceEndDate);
      setIsVisible(true);
    } else if (isPlanInGrace && isThreeYearlyPlan) {
      setBannerType(graceFromPaymentFailure ? 'threeYearlyGracePaymentFailure' : 'threeYearlyGrace');
      const expirationDate = timestampToDate(currentPlan.endDate / 1000);
      const graceEndDate = formatGraceDate(expirationDate, 'isThreeYearlyPlan');
      setGraceEndDate(graceEndDate);
      setIsVisible(true);
    } else if (isPaygPlan && isPaygExpiredState) {
      setBannerType('paygExpired');
      setIsVisible(true);
    } else if (isPaygPlan && isPaygGraceState) {
      setBannerType(resolveMonthlyStyleGraceBannerType(paymentFailureAt, { payg: true }));
      const expirationDate = timestampToDate(currentPlan.endDate / 1000);
      const graceEndDate = formatGraceDate(expirationDate, 'isMonthlyPlan');
      setGraceEndDate(graceEndDate);
      setIsVisible(true);
    } else if (isMonthlyPlan && isExpiredPlan && !isPaygPlan) {
      setBannerType('monthlyExpired');
      setIsVisible(true);
    } else if (isMonthlyPlan && isPaygGraceState && !isPaygPlan) {
      setBannerType(resolveMonthlyStyleGraceBannerType(paymentFailureAt, { payg: false }));
      const expirationDate = timestampToDate(currentPlan.endDate / 1000);
      const graceEndDate = formatGraceDate(expirationDate, 'isMonthlyPlan');
      setGraceEndDate(graceEndDate);
      setIsVisible(true);
    } else if (
      constants.SUBSCRIPTION.SHOW_LMS_PARTNER_KEYS_EXPIRED_BANNER &&
      !isInsightMonthlyUsageBillingPlan(currentPlan) &&
      isLmsCreditsAllZero(apiLmsResponse)
    ) {
      // LMS pool only: do not show "partner keys exhausted" when credit summary is already on
      // Insight monthly usage billing (MUB / PAYG) — treat as monthly subscription, not LMS keys.
      setBannerType('lmsExpired');
      setIsVisible(true);
    } else {
      setBannerType(null);
      setIsVisible(false);
    }
  }, []);

  const fetchTrialStatus = useCallback(async () => {
    // Re-entrancy guard only. All triggers go through useSubscriptionSummaryRefreshTriggers, which
    // serialises calls and queues one trailing fetch for requests that arrive mid-flight.
    if (isFetchingRef.current) {
      return;
    }
    isFetchingRef.current = true;
    try {
      // Keep existing banner visible during background refresh (focus/tab switch)
      // to avoid hide/show flicker; only show loader on first load.
      if (!hasBannerResolvedRef.current) {
        setLoading(true);
      }
      const _Id = cookies.get('_Id');

      if (!_Id) {
        setLoading(false);
        return;
      }

      // Call GET_SUBSCRIPTION_PLAN_STATUS API
      // Note: This API requires platform and currentVer headers (same as useSubscriptionPlans)
      const response = await useApi(
        'GET_SUBSCRIPTION_HISTORY',
        {},
        {
          headers: {
            platform: 'WEB',
            currentVer: ''
          }
        },
        []
      );

      const details = response?.details || {};
      summaryDetailsRef.current = details;
      applySummaryDetails(details);
    } catch (error) {
      console.error('Error fetching trial status:', error);
      // Don't show banner on error
    } finally {
      isFetchingRef.current = false;
      hasBannerResolvedRef.current = true;
      setLoading(false);
    }
  }, [applySummaryDetails]);

  // Fetch once on mount, on subscription-change events and on throttled window focus.
  // No route dependency: this banner is mounted once by MainLayout for every page, and refetching on
  // `pathname` made creditSummary fire on every navigation (left over from when banners were
  // manage-subscriptions-only; PRJCBUGEN-61427 made them global but kept the trigger).
  useSubscriptionSummaryRefreshTriggers(fetchTrialStatus);

  // Apex feature flags resolve after the first summary fetch: re-classify the cached summary
  // instead of calling creditSummary again.
  useEffect(() => {
    if (summaryDetailsRef.current) {
      applySummaryDetails(summaryDetailsRef.current);
    }
  }, [isApexEligible, isPaygMubFromApexTimestamp, applySummaryDetails]);

  useEffect(() => {
    const searchParams = new URLSearchParams(search);
    if (searchParams.get('stripe_success') === '1') {
      // Go through the trigger hook so a return during the mount fetch is queued, not dropped.
      dispatchSubscriptionSummaryRefresh();
      // Clean up the success flag so this only triggers once.
      navigate(pathname, { replace: true });
    }
  }, [search, pathname, navigate]);

  const handleClose = () => {
    setIsVisible(false);
  };

  const handleChoosePlan = (e) => {
    e.preventDefault();
    e.stopPropagation();
    navigate(`${getManageSubscriptionsPath()}?view=plans`);
  };

  const handleLmsAddKey = (e) => {
    e.preventDefault();
    e.stopPropagation();
    navigate(`${getManageSubscriptionsPath()}?view=netgear-partner&openAddKey=1`);
  };

  const handleLmsChangePlan = (e) => {
    e.preventDefault();
    e.stopPropagation();
    navigate(`${getManageSubscriptionsPath()}?view=netgear-partner&openChangePlan=1`);
  };

  const handleLmsActivatePlan = (e) => {
    e.preventDefault();
    e.stopPropagation();
    navigate(`${getManageSubscriptionsPath()}?view=netgear-partner&openActivatePlan=1`);
  };

  const lmsExpiredBannerLinkSx = {
    textDecoration: 'underline',
    fontWeight: 700,
    color: 'error.main',
    cursor: 'pointer',
    display: 'inline',
    verticalAlign: 'baseline',
    '&:hover': {
      textDecoration: 'underline',
      opacity: 0.8
    }
  } as const;

  const handleManageBillingForPayg = () => {
    if (subscriptionPlans?.monthly?.planId) {
      void startChoosePlanMonthlyStripeCheckout(subscriptionPlans.monthly);
      return;
    }
    void createStripeCheckoutSession(STRIPE_CHECKOUT_INSIGHT_PLAN_KEY, undefined, { portalReturn: true });
  };

  const handleBuyMore = () => {
    const newDeviceCredits = 1; // Minimum quantity to purchase is always 1

    if (bannerType === 'paygGrace' || bannerType === 'paygGracePaymentFailure' || bannerType === 'paygExpired') {
      handleManageBillingForPayg();
      return;
    }

    if (
      bannerType === 'monthlyGraceChoosePlan' ||
      bannerType === 'monthlyGracePaymentFailure' ||
      bannerType === 'yearlyGrace' ||
      bannerType === 'yearlyGracePaymentFailure' ||
      bannerType === 'threeYearlyGrace' ||
      bannerType === 'threeYearlyGracePaymentFailure'
    ) {
      createStripeCheckoutSession(STRIPE_CHECKOUT_INSIGHT_PLAN_KEY, newDeviceCredits);
      return;
    }

    const freq = String(currentPlanData?.frequency ?? '').trim();
    const freqToBucket: Record<string, string> = {
      '0': 'monthly',
      '1': '1year',
      '3': '3years',
      '4': '5years'
    };
    let bucket = freqToBucket[freq];
    if (!bucket && (bannerType === 'yearlyGrace' || bannerType === 'yearlyGracePaymentFailure')) bucket = '1year';
    if (!bucket && (bannerType === 'threeYearlyGrace' || bannerType === 'threeYearlyGracePaymentFailure')) bucket = '3years';

    const planObj = bucket ? subscriptionPlans[bucket] : null;
    const planKey = planObj?.planType;
    if (!planKey) {
      console.error('Cannot open checkout: planType missing from subscription plans API');
      return;
    }
    createStripeCheckoutSession(planKey, newDeviceCredits);
  };

  const choosePlanLink = (
    <Box
      component="span"
      onClick={handleChoosePlan}
      sx={{
        textDecoration: 'underline',
        fontWeight: 500,
        color: 'inherit',
        cursor: 'pointer',
        display: 'inline',
        '&:hover': {
          textDecoration: 'underline',
          opacity: 0.8
        }
      }}
    >
      {bannerType === 'yearlyExpired' || bannerType === 'threeYearlyExpired' || bannerType === 'monthlyExpired'
        ? 'Choose Plan.'
        : 'upgrade to a plan'}
    </Box>
  );

  /** Figma: trial-ending banner — bold underlined link in warning-main (#EF6C00) */
  const choosePlanTrialEndingLink = (
    <Box
      component="span"
      onClick={handleChoosePlan}
      sx={{
        textDecoration: 'underline',
        fontWeight: 700,
        color: TRIAL_ENDING_BANNER_FG,
        cursor: 'pointer',
        display: 'inline',
        '&:hover': {
          textDecoration: 'underline',
          opacity: 0.8
        }
      }}
    >
      {intl.formatMessage({
        id: 'subscriptionChooseAPlan',
        defaultMessage: 'Choose a Plan'
      })}
    </Box>
  );

  const manageBillingLink = (
    <Typography
      component="span"
      onClick={handleBuyMore}
      sx={{
        textDecoration: 'underline',
        fontWeight: 500,
        color: 'inherit',
        cursor: 'pointer',
        display: 'inline',
        '&:hover': {
          textDecoration: 'underline',
          opacity: 0.8
        }
      }}
    >
      Manage Billing
    </Typography>
  );

  const linkInlineSx = {
    textDecoration: 'underline',
    fontWeight: 500,
    color: 'inherit',
    cursor: 'pointer',
    display: 'inline',
    '&:hover': {
      textDecoration: 'underline',
      opacity: 0.8
    }
  } as const;

  const updatePaymentLink = (
    <Box component="span" onClick={handleBuyMore} sx={linkInlineSx}>
      {intl.formatMessage({
        id: 'subscriptionUpdateYourPayment',
        defaultMessage: 'Update Your Payment'
      })}
    </Box>
  );

  const choosePlanBannerLink = (
    <Box component="span" onClick={handleChoosePlan} sx={linkInlineSx}>
      {intl.formatMessage({
        id: 'subscriptionChoosePlan'
      })}
    </Box>
  );

  /** Yearly / 3-year grace banners (Figma): link label "Choose a Plan", opens plan page + choose drawer via ?view=plans */
  const choosePlanYearlyGraceLink = (
    <Box component="span" onClick={handleChoosePlan} sx={linkInlineSx}>
      {intl.formatMessage({
        id: 'subscriptionChooseAPlan',
        defaultMessage: 'Choose a Plan'
      })}
    </Box>
  );

  const paygManageBillingLink = (chunks) => (
    <Box
      component="span"
      role="button"
      tabIndex={0}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        handleManageBillingForPayg();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleManageBillingForPayg();
        }
      }}
      sx={linkInlineSx}
    >
      {chunks}
    </Box>
  );

  /** `paymentFailureAt` from API: UTC ms; non-zero implies payment-failure grace when banner applies. */
  const paymentFailureRetryDateFormatted = useMemo(() => {
    const raw = currentPlanData?.paymentFailureAt;
    const n = Number(raw);
    if (!Number.isFinite(n) || n === 0) return null;
    const d = new Date(n);
    if (Number.isNaN(d.getTime())) return null;
    return intl.formatDate(d, {
      timeZone: 'UTC',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  }, [currentPlanData?.paymentFailureAt, intl]);

  const retryPaymentSentence = useMemo(() => {
    if (bannerType !== 'monthlyGracePaymentFailure' && bannerType !== 'paygGracePaymentFailure') return '';
    if (paymentFailureRetryDateFormatted) {
      return intl.formatMessage(
        {
          id: 'subscriptionGracePaymentRetryOnDate',
          defaultMessage: 'Once updated, the system will retry the payment on {retryDate}.'
        },
        { retryDate: paymentFailureRetryDateFormatted }
      );
    }
    return intl.formatMessage({
      id: 'subscriptionGracePaymentRetryWithinFewDays',
      defaultMessage: 'Once updated, the system will retry the payment within a few days.'
    });
  }, [bannerType, paymentFailureRetryDateFormatted, intl]);

  // Don't render when banner is not needed.
  // Intentionally avoid unmounting on refresh-loading to prevent flicker.
  if (!isVisible || !bannerType || (bannerType === 'lmsExpired' && !constants.SUBSCRIPTION.SHOW_LMS_PARTNER_KEYS_EXPIRED_BANNER)) {
    return null;
  }

  const isExpiredBanner =
    bannerType === 'yearlyExpired' ||
    bannerType === 'threeYearlyExpired' ||
    bannerType === 'monthlyExpired' ||
    bannerType === 'paygExpired' ||
    bannerType === 'trialExpired' ||
    bannerType === 'lmsExpired';
  const isGraceBanner =
    bannerType === 'yearlyGrace' ||
    bannerType === 'yearlyGracePaymentFailure' ||
    bannerType === 'threeYearlyGrace' ||
    bannerType === 'threeYearlyGracePaymentFailure' ||
    bannerType === 'monthlyGraceChoosePlan' ||
    bannerType === 'monthlyGracePaymentFailure' ||
    bannerType === 'paygGrace' ||
    bannerType === 'paygGracePaymentFailure';
  const isRedStyledBanner = isExpiredBanner || isGraceBanner;
  const isTrialEndingBanner = bannerType === 'trialEnding';

  return (
    <Alert
      severity={isRedStyledBanner ? 'error' : isTrialEndingBanner ? 'warning' : 'info'}
      sx={{
        ...(isTrialEndingBanner && {
          bgcolor: theme.palette.mode === ThemeMode.DARK ? '#FFA72629' : TRIAL_ENDING_BANNER_BG_LIGHT,
          color: TRIAL_ENDING_BANNER_FG,
          borderRadius: '4px',
          alignItems: 'flex-start',
          py: 0.75,
          px: 2,
          '& .MuiAlert-icon': {
            color: TRIAL_ENDING_BANNER_FG
          },
          '& .MuiIconButton-root': {
            color: TRIAL_ENDING_BANNER_FG
          }
        }),
        '& .MuiAlert-message': {
          fontSize: '14px',
          lineHeight: isTrialEndingBanner ? 1.43 : undefined,
          letterSpacing: isTrialEndingBanner ? '0.17px' : undefined,
          ...(isTrialEndingBanner && { color: TRIAL_ENDING_BANNER_FG }),
          '& .MuiTypography-root': {
            fontSize: '14px'
          }
        }
      }}
      action={
        <IconButton aria-label="close" onClick={handleClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      }
      iconMapping={{
        warning: <AccessTimeIcon fontSize="inherit" />,
        error:
          bannerType === 'trialExpired' ? (
            <ScheduleIcon fontSize="inherit" />
          ) : isExpiredBanner ? (
            <AccessTimeIcon fontSize="inherit" />
          ) : (
            <ErrorOutline fontSize="inherit" />
          ),
        info: <AccessTimeIcon fontSize="inherit" />
      }}
      variant="standard"
    >
      {bannerType === 'trialExpired' ? (
        <Stack spacing={0} id="free-trial-expired-banner">
          <Typography component="div" variant="body2" sx={{ m: 0, color: 'error.main' }}>
            {trialExpiredDate ? (
              <FormattedMessage
                id="subscriptionFreeTrialExpiredBanner"
                values={{ date: trialExpiredDate, choosePlanLink: choosePlanYearlyGraceLink }}
              />
            ) : (
              <FormattedMessage id="subscriptionFreeTrialExpiredBannerNoDate" values={{ choosePlanLink: choosePlanYearlyGraceLink }} />
            )}
          </Typography>
          <Typography component="div" variant="body2" sx={{ m: 0, color: 'grey.800' }}>
            <FormattedMessage id="subscriptionFreeTrialExpiredBundledDevicesNote" />
          </Typography>
        </Stack>
      ) : bannerType === 'trialEnding' ? (
        <Stack spacing={0}>
          <Typography component="div" variant="body2" sx={{ m: 0, color: TRIAL_ENDING_BANNER_FG, fontWeight: 400 }}>
            {remainingTrialDays === 0 ? (
              <FormattedMessage id="subscriptionFreeTrialEndingTodayBanner" values={{ choosePlanLink: choosePlanTrialEndingLink }} />
            ) : remainingTrialDays === 1 ? (
              <FormattedMessage id="subscriptionFreeTrialEndingOneDayBanner" values={{ choosePlanLink: choosePlanTrialEndingLink }} />
            ) : (
              <FormattedMessage
                id="subscriptionFreeTrialEndingBannerNoDate"
                values={{ days: remainingTrialDays, choosePlanLink: choosePlanTrialEndingLink }}
              />
            )}
          </Typography>
          <Typography component="div" variant="body2" sx={{ m: 0, color: 'grey.800' }}>
            <FormattedMessage id="subscriptionFreeTrialExpiredBundledDevicesNote" />
          </Typography>
        </Stack>
      ) : bannerType === 'trialActive' ? (
        <Stack spacing={0} key={`trial-active-banner-${intl.locale}`}>
          <Typography component="div" variant="body2" sx={{ m: 0, color: 'inherit' }}>
            {intl.formatMessage(
              {
                id: 'subscriptionFreeTrialActiveBanner',
                defaultMessage:
                  'Our free trial will end in {expiryDate} days or when you upgrade to a new subscription plan, whichever comes first.'
              },
              { expiryDate }
            )}
          </Typography>
          <Typography component="div" variant="body2" sx={{ m: 0, color: 'inherit' }}>
            {intl.formatMessage({
              id: 'subscriptionFreeTrialActiveBundledDevicesNote',
              defaultMessage:
                'Note: Your bundled device will continue to operate for the remainder of its term without requiring a separate plan.'
            })}
          </Typography>
        </Stack>
      ) : bannerType === 'yearlyExpired' || bannerType === 'threeYearlyExpired' ? (
        <Stack spacing={0}>
          <Typography component="div" variant="body2" sx={{ m: 0, whiteSpace: 'pre-line', color: 'error.main' }}>
            <FormattedMessage
              id={bannerType === 'threeYearlyExpired' ? 'subscriptionThreeYearPlanExpiredBanner' : 'subscriptionOneYearPlanExpiredBanner'}
              defaultMessage={
                bannerType === 'threeYearlyExpired'
                  ? 'Your 3-year subscription has expired.\nPlease {choosePlanLink} to continue managing your devices.'
                  : 'Your yearly subscription has expired.\nPlease {choosePlanLink} to continue managing your devices.'
              }
              values={{ choosePlanLink: choosePlanYearlyGraceLink }}
            />
          </Typography>
          <Typography component="div" variant="body2" sx={{ m: 0, color: 'grey.800' }}>
            <FormattedMessage id="subscriptionGraceBannerBundledDevicesNote" />
          </Typography>
        </Stack>
      ) : bannerType === 'yearlyGracePaymentFailure' || bannerType === 'threeYearlyGracePaymentFailure' ? (
        <Stack spacing={0}>
          <Typography component="div" variant="body2" sx={{ m: 0, whiteSpace: 'pre-line', color: 'error.main' }}>
            <FormattedMessage
              id="subscriptionYearlyPlanGracePaymentFailureBannerBody"
              values={{ graceEndDate, choosePlanLink: choosePlanYearlyGraceLink }}
            />
          </Typography>
          <Typography component="div" variant="body2" sx={{ m: 0, color: 'grey.800' }}>
            <FormattedMessage id="subscriptionGraceBannerBundledDevicesNote" />
          </Typography>
        </Stack>
      ) : bannerType === 'yearlyGrace' || bannerType === 'threeYearlyGrace' ? (
        <Stack spacing={0}>
          <Typography component="div" variant="body2" sx={{ m: 0, whiteSpace: 'pre-line', color: 'error.main' }}>
            <FormattedMessage id="subscriptionYearlyPlanGraceBanner" values={{ graceEndDate, choosePlanLink: choosePlanYearlyGraceLink }} />
          </Typography>
          <Typography component="div" variant="body2" sx={{ m: 0, color: 'grey.800' }}>
            <FormattedMessage id="subscriptionGraceBannerBundledDevicesNote" />
          </Typography>
        </Stack>
      ) : bannerType === 'paygGracePaymentFailure' ? (
        <Stack spacing={0} id="payg-grace-payment-failure-banner">
          <Typography component="div" variant="body2" sx={{ m: 0, whiteSpace: 'pre-line', color: 'error.main' }}>
            {graceEndDate ? (
              <FormattedMessage
                id="subscriptionPaygGracePaymentFailureBannerBody"
                values={{ graceEndDate, manageBillingLink: paygManageBillingLink, retryPaymentSentence }}
              />
            ) : (
              <>
                <FormattedMessage
                  id="subscriptionPaygGracePaymentFailureBannerBodyNoDate"
                  values={{ manageBillingLink: paygManageBillingLink }}
                />
                {retryPaymentSentence ? (
                  <>
                    {'\n'}
                    {retryPaymentSentence}
                  </>
                ) : null}
              </>
            )}
          </Typography>
          <Typography component="div" variant="body2" sx={{ m: 0, color: 'grey.800' }}>
            <FormattedMessage id="subscriptionGraceBannerBundledDevicesNote" />
          </Typography>
        </Stack>
      ) : bannerType === 'paygGrace' ? (
        <Stack spacing={0} id="payg-grace-banner">
          <Typography component="div" variant="body2" sx={{ m: 0, whiteSpace: 'pre-line', color: 'error.main' }}>
            <FormattedMessage
              id={graceEndDate ? 'subscriptionPaygGraceBannerBody' : 'subscriptionPaygGraceBannerBodyNoDate'}
              values={{ graceEndDate, manageBillingLink: paygManageBillingLink }}
            />
          </Typography>
          <Typography component="div" variant="body2" sx={{ m: 0, color: 'grey.800' }}>
            <FormattedMessage id="subscriptionGraceBannerBundledDevicesNote" />
          </Typography>
        </Stack>
      ) : bannerType === 'paygExpired' ? (
        <Stack spacing={0} id="payg-expired-banner">
          <Typography component="div" variant="body2" sx={{ m: 0, whiteSpace: 'pre-line', color: 'error.main' }}>
            <FormattedMessage id="subscriptionPaygExpiredBannerBody" values={{ manageBillingLink: paygManageBillingLink }} />
          </Typography>
          <Typography component="div" variant="body2" sx={{ m: 0, color: 'grey.800' }}>
            <FormattedMessage id="subscriptionGraceBannerBundledDevicesNote" />
          </Typography>
        </Stack>
      ) : bannerType === 'monthlyGracePaymentFailure' ? (
        <Stack spacing={0}>
          <Typography component="div" variant="body2" sx={{ m: 0, whiteSpace: 'pre-line', color: 'error.main' }}>
            <FormattedMessage
              id="subscriptionMonthlyGracePaymentFailureBannerBody"
              values={{ graceEndDate, updatePaymentLink, retryPaymentSentence }}
            />
          </Typography>
          <Typography component="div" variant="body2" sx={{ m: 0, color: 'grey.800' }}>
            <FormattedMessage id="subscriptionGraceBannerBundledDevicesNote" />
          </Typography>
        </Stack>
      ) : bannerType === 'monthlyGraceChoosePlan' ? (
        <Stack spacing={0}>
          <Typography component="div" variant="body2" sx={{ m: 0, whiteSpace: 'pre-line', color: 'error.main' }}>
            <FormattedMessage
              id="subscriptionMonthlyGraceChoosePlanBannerBody"
              values={{ graceEndDate, choosePlanLink: choosePlanYearlyGraceLink }}
            />
          </Typography>
          <Typography component="div" variant="body2" sx={{ m: 0, color: 'grey.800' }}>
            <FormattedMessage id="subscriptionGraceBannerBundledDevicesNote" />
          </Typography>
        </Stack>
      ) : bannerType === 'monthlyExpired' ? (
        <Stack spacing={0}>
          <Typography component="div" variant="body2" sx={{ m: 0, whiteSpace: 'pre-line', color: 'error.main' }}>
            <FormattedMessage
              id="subscriptionMonthlyPlanExpiredBanner"
              defaultMessage={'Your monthly subscription has expired.\nPlease {choosePlanLink} to continue managing your devices.'}
              values={{ choosePlanLink: choosePlanYearlyGraceLink }}
            />
          </Typography>
          <Typography component="div" variant="body2" sx={{ m: 0, color: 'grey.800' }}>
            <FormattedMessage id="subscriptionGraceBannerBundledDevicesNote" />
          </Typography>
        </Stack>
      ) : bannerType === 'lmsExpired' ? (
        <Stack spacing={0} id="lms-partner-keys-expired-banner">
          <Typography component="div" variant="body2" sx={{ m: 0, color: 'error.main' }}>
            <FormattedMessage
              id="subscriptionLmsPartnerKeysExpiredBannerLine1"
              values={{
                addKey: (chunks) =>
                  canAddLmsKey ? (
                    <Box
                      component="span"
                      role="button"
                      tabIndex={0}
                      onClick={handleLmsAddKey}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleLmsAddKey(e);
                        }
                      }}
                      sx={lmsExpiredBannerLinkSx}
                    >
                      {chunks}
                    </Box>
                  ) : (
                    <Box component="span" sx={{ fontWeight: 700, color: 'error.main' }}>
                      {chunks}
                    </Box>
                  )
              }}
            />
          </Typography>
          <Typography component="div" variant="body2" sx={{ m: 0, color: 'error.main' }}>
            <FormattedMessage
              id="subscriptionLmsPartnerKeysExpiredBannerLine2"
              values={{
                changePlan: (chunks) => {
                  if (canChangePlan && isCountrySupported) {
                    return (
                      <Box
                        component="span"
                        role="button"
                        tabIndex={0}
                        onClick={handleLmsChangePlan}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleLmsChangePlan(e);
                          }
                        }}
                        sx={lmsExpiredBannerLinkSx}
                      >
                        {chunks}
                      </Box>
                    );
                  }
                  if (canAddLmsKey && !isCountrySupported) {
                    return (
                      <Box
                        component="span"
                        role="button"
                        tabIndex={0}
                        onClick={handleLmsActivatePlan}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleLmsActivatePlan(e);
                          }
                        }}
                        sx={lmsExpiredBannerLinkSx}
                      >
                        {chunks}
                      </Box>
                    );
                  }
                  return (
                    <Box component="span" sx={{ fontWeight: 700, color: 'error.main' }}>
                      {chunks}
                    </Box>
                  );
                }
              }}
            />
          </Typography>
          <Typography component="div" variant="body2" sx={{ m: 0, color: 'grey.800' }}>
            <FormattedMessage id="subscriptionGraceBannerBundledDevicesNote" />
          </Typography>
        </Stack>
      ) : null}
    </Alert>
  );
}
